import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { SERVICE_TYPE_CODES } from '../mock';
import { money, pct, fullName, fmtDob } from '../lib/data';
import { usePatients, useProviders, usePayers, useRunEligibility } from '../api/hooks';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from '../lib/aiToast';
import StatusPill from '../components/StatusPill';
import RiskBadge from '../components/RiskBadge';
import AgentChip from '../components/AgentChip';
import Confidence from '../components/Confidence';
import Pipeline from '../components/Pipeline';
import AddProvider from '../components/AddProvider';
import { Plus } from 'lucide-react';

const STAGES = ['Ingested', '270 sent', '271 received', 'Parsed', 'Risk scored', 'Routed'];

export default function NewEligibility() {
  const { session } = useAuth();
  const nav = useNavigate();
  const practiceId = session?.practice?.id || 'prac_001';

  const { data: patients = [] } = usePatients(practiceId);
  const { data: providers = [] } = useProviders(practiceId);
  const { data: payers = [] } = usePayers();
  const run = useRunEligibility();

  const [form, setForm] = useState({
    patientId: '', providerId: '', serviceTypeCode: '30', dateOfService: '',
  });
  const [stage, setStage] = useState(-1); // -1 idle, 0..5 running, 6 done
  const [result, setResult] = useState(null);
  const [addProviderOpen, setAddProviderOpen] = useState(false);
  const timers = useRef([]);
  const providerDefaulted = useRef(false);

  // Default the provider ONCE when providers first load. Guarded so it can never
  // overwrite a later selection (e.g. a freshly added provider auto-selected via
  // the Add-provider dialog).
  useEffect(() => {
    if (!providerDefaulted.current && providers[0]?.id) {
      providerDefaulted.current = true;
      setForm((f) => (f.providerId ? f : { ...f, providerId: providers[0].id }));
    }
  }, [providers]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const running = stage >= 0 && stage < STAGES.length;

  const patient = useMemo(() => patients.find((p) => p.id === form.patientId), [patients, form.patientId]);
  const payerNameById = useMemo(() => new Map(payers.map((p) => [p.id, p.name])), [payers]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patientId) { toast.error('Select a patient first'); return; }
    setResult(null);
    setStage(0);
    timers.current.forEach(clearTimeout);
    timers.current = STAGES.map((_, i) => setTimeout(() => setStage(i + 1), 350 * (i + 1)));

    try {
      const res = await run.mutateAsync({
        patientId: form.patientId,
        providerId: form.providerId || undefined,
        serviceTypeCodes: form.serviceTypeCode ? [form.serviceTypeCode] : undefined,
      });
      const check = res?.check || null;
      const risk = res?.risk || {};
      if (!check) {
        toast.error('No eligibility check available for this patient');
        setStage(-1);
        return;
      }
      setResult({
        id: check.id,
        planNameReturned: check.planNameReturned,
        networkStatusResolved: check.networkStatusResolved,
        summaryText: check.summaryText,
        level: risk.level,
        coverage: check.coverage,
        financials: check.financials,
        payerName: payerNameById.get(check.payerId) || payerNameById.get(patient?.payerId),
      });
      toast.success('Eligibility check complete');
    } catch (err) {
      timers.current.forEach(clearTimeout);
      setStage(-1);
      toast.error('Eligibility check failed');
    }
  };

  const steps = STAGES.map((label, i) => ({
    label,
    status: stage > i ? 'done' : stage === i ? 'active' : 'pending',
  }));

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => nav(-1)}><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Run eligibility check</h1>
          <p className="text-sm text-slate-500">Submits a real-time 270 via Stedi, then agents parse and triage the 271.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Form */}
        <div className="dh-card p-5">
          <div className="text-sm font-semibold text-slate-900 mb-4">Patient & payer</div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Patient</Label>
              <Select value={form.patientId} onValueChange={(v) => setField('patientId', v)}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select patient" /></SelectTrigger>
                <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{fullName(p)} · ••••{p.memberIdLast4}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date of birth</Label><Input className="mt-1.5" value={patient ? fmtDob(patient.dob) : ''} readOnly placeholder="—" /></div>
              <div><Label>Member ID</Label><Input className="mt-1.5" value={patient ? `••••${patient.memberIdLast4}` : ''} readOnly placeholder="—" /></div>
            </div>
            <div>
              <Label>Payer</Label>
              <Input className="mt-1.5" value={patient ? (payerNameById.get(patient.payerId) || '—') : ''} readOnly placeholder="Select a patient" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Rendering provider (NPI)</Label>
                <button type="button" onClick={() => setAddProviderOpen(true)} className="text-xs font-medium text-brand-600 hover:text-brand-700 inline-flex items-center gap-1">
                  <Plus className="h-3 w-3" />Add provider
                </button>
              </div>
              <Select key={`prov-${providers.length}`} value={form.providerId} onValueChange={(v) => setField('providerId', v)}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select provider" /></SelectTrigger>
                <SelectContent>{providers.map((p) => <SelectItem key={p.id} value={p.id}>Dr. {p.firstName} {p.lastName} · {p.npi}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Service type</Label>
                <Select value={form.serviceTypeCode} onValueChange={(v) => setField('serviceTypeCode', v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICE_TYPE_CODES.map((s) => <SelectItem key={s.code} value={s.code}>{s.code} — {s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date of service</Label><Input type="date" className="mt-1.5" value={form.dateOfService} onChange={(e) => setField('dateOfService', e.target.value)} /></div>
            </div>
            <Button type="submit" className="w-full bg-brand-600 hover:bg-brand-700 text-white" disabled={running}>
              {running ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running pipeline…</> : <><ShieldCheck className="h-4 w-4 mr-2" />Submit eligibility check</>}
            </Button>
          </form>
        </div>

        {/* Result / pipeline */}
        <div className="dh-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-semibold text-slate-900">Agent pipeline</div>
            {(running || result) && <AgentChip agent="verification" size="xs" />}
          </div>

          {stage === -1 && !result && (
            <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl p-8 text-center">
              Submit a check to watch the agents ingest, run 270/271, parse benefits, score risk, and route.
            </div>
          )}

          {(running || result) && (
            <div className="dh-inset p-4 mb-4"><Pipeline steps={steps} /></div>
          )}

          {result && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500">Plan returned</div>
                  <div className="font-semibold text-slate-900">{result.planNameReturned}</div>
                  <div className="text-xs text-slate-500">{result.payerName}</div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <StatusPill status={result.networkStatusResolved} />
                  <RiskBadge level={result.level} />
                </div>
              </div>
              <div className="ai-surface p-3.5 text-sm text-slate-800">
                <div className="flex items-center gap-2 mb-1.5"><AgentChip agent="verification" size="xs" /><Confidence value={0.9} label="parse" /></div>
                {result.summaryText}
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="dh-inset p-3"><div className="text-slate-500">Copay</div><div className="font-semibold text-slate-900 mt-1 tnum">{money(result.financials.copay)}</div></div>
                <div className="dh-inset p-3"><div className="text-slate-500">Deductible</div><div className="font-semibold text-slate-900 mt-1 tnum">{money(result.financials.deductibleIndMet)}/{money(result.financials.deductibleInd)}</div></div>
                <div className="dh-inset p-3"><div className="text-slate-500">Coinsurance</div><div className="font-semibold text-slate-900 mt-1 tnum">{pct(result.financials.coinsurance)}</div></div>
              </div>
              <div className="flex gap-2">
                {result.id && <Button size="sm" variant="outline" onClick={() => nav(`/app/eligibility/${result.id}`)}>Open verification</Button>}
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => nav('/app')}>Save & back to worklist</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AddProvider
        open={addProviderOpen}
        onClose={() => setAddProviderOpen(false)}
        practiceId={practiceId}
        onCreated={(p) => setField('providerId', p.id)}
      />
    </div>
  );
}
